import { defineComponent, h, mergeProps, ref, watch, } from 'vue';
import { WEFAT_COMPONENT_DOM_VERSION } from './basic-components.js';
import { WefatComponentRuntimeError } from './component-error.js';
export const WEFAT_IMAGE_MODES = Object.freeze([
    'scaleToFill',
    'aspectFit',
    'aspectFill',
    'widthFix',
    'heightFix',
    'top',
    'bottom',
    'center',
    'left',
    'right',
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
]);
const SAFE_IMAGE_PROTOCOLS = new Set(['blob:', 'http:', 'https:']);
const IMAGE_PROTOCOL = /^([A-Za-z][A-Za-z\d+.-]*):/;
function containsUrlControlCharacter(value) {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 31 || code === 127)
            return true;
    }
    return false;
}
const IMAGE_MODE_STYLES = Object.freeze({
    scaleToFill: Object.freeze({ objectFit: 'fill', objectPosition: 'center' }),
    aspectFit: Object.freeze({ objectFit: 'contain', objectPosition: 'center' }),
    aspectFill: Object.freeze({ objectFit: 'cover', objectPosition: 'center' }),
    widthFix: Object.freeze({ height: 'auto', objectFit: 'fill', objectPosition: 'center' }),
    heightFix: Object.freeze({ width: 'auto', objectFit: 'fill', objectPosition: 'center' }),
    top: Object.freeze({ objectFit: 'none', objectPosition: 'top' }),
    bottom: Object.freeze({ objectFit: 'none', objectPosition: 'bottom' }),
    center: Object.freeze({ objectFit: 'none', objectPosition: 'center' }),
    left: Object.freeze({ objectFit: 'none', objectPosition: 'left' }),
    right: Object.freeze({ objectFit: 'none', objectPosition: 'right' }),
    topLeft: Object.freeze({ objectFit: 'none', objectPosition: 'left top' }),
    topRight: Object.freeze({ objectFit: 'none', objectPosition: 'right top' }),
    bottomLeft: Object.freeze({ objectFit: 'none', objectPosition: 'left bottom' }),
    bottomRight: Object.freeze({ objectFit: 'none', objectPosition: 'right bottom' }),
});
export function resolveWefatImageModeStyle(mode) {
    if (!WEFAT_IMAGE_MODES.some((candidate) => candidate === mode)) {
        throw new WefatComponentRuntimeError('WF5001', `Unsupported image mode: ${mode || '<empty>'}`, `Use one of the supported image modes: ${WEFAT_IMAGE_MODES.join(', ')}.`);
    }
    return IMAGE_MODE_STYLES[mode];
}
export function resolveWefatImageSource(source) {
    if (typeof source !== 'string') {
        throw new WefatComponentRuntimeError('WF5006', 'Image source must be a string', 'Pass a relative URL, HTTP(S) URL, Blob URL, or image data URL to image src.');
    }
    if (containsUrlControlCharacter(source)) {
        throw new WefatComponentRuntimeError('WF5006', 'Image source contains a control character', 'Remove control characters from the image URL before assigning image src.');
    }
    const protocol = IMAGE_PROTOCOL.exec(source)?.[1]?.toLowerCase();
    if (protocol === undefined || SAFE_IMAGE_PROTOCOLS.has(`${protocol}:`))
        return source;
    if (protocol === 'data' && source.slice(0, 11).toLowerCase().startsWith('data:image/')) {
        return source;
    }
    throw new WefatComponentRuntimeError('WF5006', `Unsupported image source protocol: ${protocol}:`, 'Use a relative URL, HTTP(S) URL, Blob URL, or image data URL. Provide an explicit adapter for Mini Program temporary-file URLs.');
}
function imageLoadDetail(event) {
    const image = event.currentTarget;
    return Object.freeze({
        width: image?.naturalWidth ?? 0,
        height: image?.naturalHeight ?? 0,
    });
}
function imageErrorDetail(error) {
    return Object.freeze({
        errMsg: error === undefined
            ? 'image:fail resource could not be loaded'
            : `image:fail ${error.message}`,
    });
}
export const WefatImage = defineComponent({
    name: 'WefatImage',
    inheritAttrs: false,
    props: {
        hidden: {
            type: Boolean,
            default: false,
        },
        lazyLoad: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'scaleToFill',
        },
        referrerPolicy: {
            type: String,
            default: '',
        },
        showMenuByLongpress: {
            type: Boolean,
            default: false,
        },
        src: {
            type: String,
            default: '',
        },
    },
    emits: {
        error: (detail) => typeof detail.errMsg === 'string',
        load: (detail) => (Number.isFinite(detail.width) && Number.isFinite(detail.height)),
    },
    setup(props, { attrs, emit }) {
        const safeSource = ref('');
        watch(() => props.src, (source) => {
            try {
                safeSource.value = resolveWefatImageSource(source);
            }
            catch (error) {
                safeSource.value = '';
                if (error instanceof WefatComponentRuntimeError)
                    emit('error', imageErrorDetail(error));
                else
                    throw error;
            }
        }, { immediate: true });
        return () => h('img', mergeProps(attrs, {
            alt: typeof attrs.alt === 'string' ? attrs.alt : '',
            class: 'wefat-image',
            'data-wefat-component': 'image',
            'data-wefat-component-version': String(WEFAT_COMPONENT_DOM_VERSION),
            'data-wefat-image-mode': props.mode,
            'data-wefat-image-longpress-menu': props.showMenuByLongpress ? 'enabled' : 'disabled',
            draggable: false,
            hidden: props.hidden ? true : undefined,
            loading: props.lazyLoad ? 'lazy' : attrs.loading,
            onContextmenu: (event) => {
                if (!props.showMenuByLongpress)
                    event.preventDefault();
            },
            onError: () => emit('error', imageErrorDetail()),
            onLoad: (event) => emit('load', imageLoadDetail(event)),
            referrerPolicy: props.referrerPolicy || undefined,
            src: safeSource.value || undefined,
            style: [
                resolveWefatImageModeStyle(props.mode),
                { WebkitTouchCallout: props.showMenuByLongpress ? 'default' : 'none' },
            ],
        }));
    },
});
//# sourceMappingURL=image-component.js.map